import { css } from "@emotion/css";
import { CircularProgress } from "@octopusdeploy/design-system-components";
import { ExclamationTriangleIcon, CheckCircleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { MachineModelHealthStatus } from "@octopusdeploy/octopus-server-client";
import { MachineModelHealthStatusValues } from "@octopusdeploy/octopus-server-client";
import classNames from "classnames";
import * as React from "react";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
type DiscoveryProgressProps = React.PropsWithChildren<{
    name: string;
    isFound: boolean;
    healthStatus: MachineModelHealthStatus;
    showHelpLink?: boolean;
    bearerTokenExpired?: boolean;
}>;
export const DiscoveryProgress = ({ showHelpLink = true, ...props }: DiscoveryProgressProps) => {
    let boxStyle = "";
    let healthcheckContent: JSX.Element | null = null;
    const connectionContent: JSX.Element = (<>
            {props.isFound ? (<>
                    <div className={styles.iconContainer}>
                        <CheckCircleIcon size={20} color={themeTokens.color.icon.success}/>
                    </div>
                    <span>
                        <b>{props.name}</b> established a connection
                    </span>
                </>) : (<>
                    <div className={styles.iconContainer}>
                        <CircularProgress size="container" aria-hidden="true"/>
                    </div>
                    <span>
                        Waiting for <b>{props.name}</b> to establish a connection...
                    </span>
                </>)}
        </>);
    if (props.healthStatus !== MachineModelHealthStatusValues.Unknown) {
        if (props.healthStatus === MachineModelHealthStatusValues.Unhealthy || props.healthStatus === MachineModelHealthStatusValues.Unavailable) {
            boxStyle = styles.failure;
            healthcheckContent = (<>
                    <div className={styles.iconContainer}>
                        <em className={classNames("fa-solid fa-exclamation-triangle", styles.alertIcon)}/>
                    </div>
                    <span>
                        <b>{props.name}</b> health check failed
                        {showHelpLink && (<>
                                . For help, <ExternalLink href={"kubernetes-agent"}>read the docs</ExternalLink>
                            </>)}
                    </span>
                </>);
        }
        else {
            boxStyle = styles.success;
            healthcheckContent = (<>
                    <div className={styles.iconContainer}>
                        <CheckCircleIcon size={20} color={themeTokens.color.icon.success}/>
                    </div>

                    <span>
                        <b>{props.name}</b> is healthy
                    </span>
                </>);
        }
    }
    else if (props.bearerTokenExpired) {
        boxStyle = styles.failure;
        healthcheckContent = (<>
                <div className={styles.iconContainer}>
                    <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.danger}/>
                </div>
                <span>
                    <strong>Your bearer token expired</strong> before the agent could establish a connection. To try again, add another Kubernetes Agent.
                </span>
            </>);
    }
    else {
        boxStyle = styles.waiting;
        healthcheckContent = props.isFound ? (<>
                <div className={styles.iconContainer}>
                    <CircularProgress size="small" aria-hidden="true"/>
                </div>
                <span>Performing health check...</span>
            </>) : null;
    }
    return (<div className={classNames(styles.box, boxStyle)} role="alert">
            <div className={styles.contentContainer}>{connectionContent}</div>
            {healthcheckContent && <div className={styles.contentContainer}>{healthcheckContent}</div>}
            {props.children && <div>{props.children}</div>}
        </div>);
};
DiscoveryProgress.displayName = "TentacleDiscoveryProgress";
const styles = {
    box: css({
        padding: `${space[12]} ${space[16]}`,
        alignSelf: "stretch",
        display: "grid",
        marginTop: space[16],
        fontSize: "14/16rem",
        color: themeTokens.color.text.primary,
        borderRadius: borderRadius.small,
    }),
    waiting: css({
        backgroundColor: themeTokens.color.callout.background.info.default,
    }),
    success: css({
        backgroundColor: themeTokens.color.callout.background.success.default,
    }),
    failure: css({
        backgroundColor: themeTokens.color.callout.background.danger.default,
    }),
    contentContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
        "&:not(:last-child)": {
            marginBottom: space[16],
        },
    }),
    iconContainer: css({
        padding: `${space[4]} ${space[2]} 0 ${space[2]}`,
        marginRight: space[4],
        width: "24px",
    }),
    alertIcon: css({
        fontSize: "23px",
        color: themeTokens.color.icon.danger,
    }),
};
